// ** React Imports
import { type ForwardedRef, forwardRef } from 'react'

// ** Third Party Imports
import * as Form from '@radix-ui/react-form'

// ** Styled Components
import {
	StyledActionAnchor,
	StyledActionButton,
	StyledActionLink
} from './styles'
import { Loader } from '../Loader'

interface IProps {
	to?: string
	href?: string
	form?: string
	style?: React.CSSProperties | undefined
	type?: 'button' | 'submit'
	size?: 'normal' | 'small'
	variant?: 'primary' | 'secondary' | 'text'
	label?: string
	color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning'
	onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
	className?: string
	disabled?: boolean
	fullWidth?: boolean
	startIcon?: JSX.Element
	isLoading?: boolean
}

export const ActionButton = forwardRef<
	HTMLButtonElement | HTMLAnchorElement,
	IProps
>(
	(
		{
			to,
			href,
			size = 'normal',
			type = 'button',
			style,
			label,
			color = 'primary',
			variant = 'primary',
			onClick,
			className,
			disabled = false,
			fullWidth = false,
			startIcon,
			isLoading,
			...commonProps
		},
		ref
	) => {
		if (to !== undefined) {
			return (
				<StyledActionLink
					to={to}
					ref={ref as ForwardedRef<HTMLAnchorElement>}
					size={size}
					style={style}
					color={color}
					variant={variant}
					onClick={onClick}
					className={className}
					$fullWidth={fullWidth}
					disabled={disabled}
					{...commonProps}
				>
					{!isLoading && startIcon !== undefined && startIcon}
					{!isLoading && label !== undefined && label}
					{isLoading && <Loader styles={{ width: '16px', height: '16px'}} divStyles={{ borderWidth: '1px' }} />}
				</StyledActionLink>
			)
		}

		if (href !== undefined) {
			return (
				<StyledActionAnchor
					ref={ref as ForwardedRef<HTMLAnchorElement>}
					href={href}
					size={size}
					style={style}
					color={color}
					target='_blank'
					rel='noopener noreferrer'
					variant={variant}
					className={className}
					$fullWidth={fullWidth}
					disabled={disabled}
					{...commonProps}
				>
					{!isLoading && startIcon !== undefined && startIcon}
					{!isLoading && label !== undefined && label}
					{isLoading && <Loader styles={{ width: '16px', height: '16px'}} divStyles={{ borderWidth: '1px' }} />}
				</StyledActionAnchor>
			)
		}

		return (
			<>
				{type === 'submit' ? (
					<Form.Submit asChild>
						<StyledActionButton
							ref={ref as ForwardedRef<HTMLButtonElement>}
							size={size}
							type='submit'
							style={style}
							color={color}
							variant={variant}
							className={className}
							$fullWidth={fullWidth}
							disabled={disabled}
							{...commonProps}
						>
							{!isLoading && startIcon !== undefined && startIcon}
							{!isLoading && label !== undefined && label}
							{isLoading && <Loader styles={{ width: '16px', height: '16px'}} divStyles={{ borderWidth: '1px' }} />}
						</StyledActionButton>
					</Form.Submit>
				) : (
					<StyledActionButton
						ref={ref as ForwardedRef<HTMLButtonElement>}
						size={size}
						type='button'
						style={style}
						color={color}
						variant={variant}
						onClick={onClick}
						className={className}
						$fullWidth={fullWidth}
						disabled={disabled}
						{...commonProps}
					>
						{!isLoading && startIcon !== undefined && startIcon}
						{!isLoading && label !== undefined && label}
						{isLoading && <Loader styles={{ width: '16px', height: '16px'}} divStyles={{ borderWidth: '1px' }} />}
					</StyledActionButton>
				)}
			</>
		)
	}
)

